import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brain, Code, GraduationCap, Shield, Rocket, Building, Building2, User, CheckCircle, Sparkles, Globe, Zap, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { heroData, pillars, audiences, whyUs, useCases } from "@/data/mock";

const iconMap = {
  Brain: Brain,
  Code: Code,
  Shield: Shield,
  GraduationCap: GraduationCap,
  Rocket: Rocket,
  Building: Building,
  Building2: Building2,
  User: User
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-900/95 to-slate-900" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/80 border border-slate-700 mb-8 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-slate-300">Global AI & Technology Company</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {heroData.headline.split(",").map((part, i) => (
              <span key={i}>
                {i === 0 ? (
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">{part}</span>
                ) : part}
                {i < heroData.headline.split(",").length - 1 && ","}
              </span>
            ))}
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            {heroData.subheadline}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/consultation">
              <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold px-8 py-6 text-base group">
                {heroData.primaryCta}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href="https://academy.botverseweb.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-6 text-base">
                {heroData.secondaryCta}
              </Button>
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-slate-500">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              <span className="text-sm">6 Countries</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-cyan-400" />
              <span className="text-sm">AI-Powered Solutions</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-cyan-400" />
              <span className="text-sm">Industry-Aligned</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-slate-600 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-cyan-400 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

// What We Do Section
const WhatWeDoSection = () => {
  return (
    <section className="py-24 bg-slate-900 relative">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What We Do</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Four core pillars that drive business transformation
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {pillars.map((pillar, index) => {
            const Icon = iconMap[pillar.icon];
            return (
              <Card
                key={pillar.id}
                className="group bg-slate-800/50 border-slate-700 hover:border-cyan-500/50 transition-all duration-300 hover:-translate-y-2 overflow-hidden"
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-teal-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{pillar.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{pillar.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Who We Work With Section
const AudienceSection = () => {
  return (
    <section className="py-24 bg-slate-950 relative">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Who We Work With</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            From ambitious startups to global enterprises, we partner with organizations ready to embrace AI
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {audiences.map((audience) => {
            const Icon = iconMap[audience.icon];
            return (
              <div
                key={audience.id}
                className="group p-6 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-cyan-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                  <Icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{audience.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{audience.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Why Botverse Web Section
const WhyUsSection = () => {
  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background Accent */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/3 h-2/3 bg-gradient-to-l from-cyan-500/5 to-transparent blur-3xl" />
      
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Why Choose <span className="text-cyan-400">Botverse Web</span>?
            </h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              We're not just consultants or educators — we're builders who understand the practical challenges of implementing AI in real-world business environments.
            </p>
            <div className="space-y-4">
              {whyUs.map((item) => (
                <div key={item.id} className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">{item.title}</h4>
                    <p className="text-slate-400 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">6+</div>
                  <div className="text-slate-400 text-sm">Countries</div>
                </div>
                <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">200+</div>
                  <div className="text-slate-400 text-sm">Professionals Trained</div>
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">50+</div>
                  <div className="text-slate-400 text-sm">Projects Delivered</div>
                </div>
                <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">95%</div>
                  <div className="text-slate-400 text-sm">Client Satisfaction</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Use Cases Section
const UseCasesSection = () => {
  return (
    <section className="py-24 bg-slate-950">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Featured Outcomes</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Real results from real projects
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {useCases.map((useCase) => (
            <Card key={useCase.id} className="bg-slate-900/50 border-slate-800 overflow-hidden group hover:border-cyan-500/30 transition-colors">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-cyan-400 mb-2">{useCase.metric}</div>
                <div className="text-sm text-cyan-400/70 mb-4">{useCase.metricLabel}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{useCase.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{useCase.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-transparent to-teal-500/10" />
      
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Build With AI?
          </h2>
          <p className="text-slate-400 mb-10 text-lg">
            Whether you're looking to automate processes, build intelligent products, or upskill your team — we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/consultation">
              <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold px-8 group">
                Book a Consultation
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href="https://academy.botverseweb.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8">
                Start Learning
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <HeroSection />
      <WhatWeDoSection />
      <AudienceSection />
      <WhyUsSection />
      <UseCasesSection />
      <CTASection />
    </>
  );
}
